/**
 * The shapes of a picture packed as numbers for the graphics card: one **instance** of
 * {@link FLOATS} numbers per shape. The card then draws every shape with one call: a small
 * square stretched around each shape, whose pixels the fragment shader (`shaders.ts`) colours
 * by measuring how far they are from the shape's edge.
 *
 * Each shape is described in its own frame: centre, half sizes and the direction of its x axis
 * (so lines and arrow heads point anywhere). The layout of one instance:
 *
 * | Numbers | Meaning |
 * |---|---|
 * | 0–3 | centre x, y; half width, half height |
 * | 4–7 | x axis (cos, sin); {@link ShapeKind}; first setting |
 * | 8–9 | second setting; outline width |
 * | 10–13 | fill colour (premultiplied: red, green and blue already times the opacity) |
 * | 14–17 | outline colour (premultiplied) |
 *
 * The two settings: a box's corner radius; a dash's drawn length and gap (circles and lines);
 * a chevron's line width.
 */
import type { FlatShape, Paint } from "../shapes";
import { TRANSPARENT, type ColorReader, type Rgba } from "./colors";

/** How many numbers describe one shape. */
export const FLOATS = 18;

/** The shapes the shader knows, by number (the same numbers as in `shaders.ts`). */
export const ShapeKind = {
  circle: 0,
  box: 1,
  hexagon: 2,
  triangle: 3,
  segment: 4,
  roundSegment: 5,
  chevron: 6,
} as const;

/** Packed shapes, ready to send to the graphics card. */
export interface Instances {
  /** The numbers; only the first `count * FLOATS` are used. */
  data: Float32Array<ArrayBuffer>;
  /** How many shapes. */
  count: number;
}

/** A colour times an opacity, premultiplied. */
function premultiplied([r, g, b, a]: Rgba, alpha: number): Rgba {
  const k = a * alpha;
  return [r * k, g * k, b * k, k];
}

/**
 * Packs `shapes` into `into` when it is big enough (it is reused from frame to frame), or into a
 * bigger array. Shapes that would not show (fully transparent) are left out.
 */
export function packShapes(shapes: readonly FlatShape[], color: ColorReader, into?: Float32Array<ArrayBuffer>): Instances {
  const data = into && into.length >= shapes.length * FLOATS ? into : new Float32Array(Math.max(shapes.length, 64) * FLOATS * 1.5);
  let count = 0;
  for (const s of shapes) {
    const paint = s.paint;
    if (paint.alpha <= 0) continue;
    const at = count * FLOATS;
    if (!place(data, at, s)) continue;
    colours(data, at, s, paint, color);
    count++;
  }
  return { data, count };
}

/**
 * Writes where the shape is (numbers 0 to 9 of its instance). Returns `false` for shapes too
 * small to see.
 */
function place(d: Float32Array, at: number, s: FlatShape): boolean {
  /** Writes the frame and the settings. */
  const set = (x: number, y: number, hx: number, hy: number, ax: number, ay: number, kind: number, p0: number, p1: number, width: number) => {
    d[at] = x;
    d[at + 1] = y;
    d[at + 2] = hx;
    d[at + 3] = hy;
    d[at + 4] = ax;
    d[at + 5] = ay;
    d[at + 6] = kind;
    d[at + 7] = p0;
    d[at + 8] = p1;
    d[at + 9] = width;
  };
  const width = s.paint.stroke ? s.paint.width : 0;
  const [on, off] = s.paint.dash ?? [0, 0];
  switch (s.type) {
    case "circle":
      set(s.x, s.y, s.r, s.r, 1, 0, ShapeKind.circle, on, off, width);
      return s.r > 0;
    case "box":
      set(s.x, s.y, s.hw, s.hh, Math.cos(s.angle), Math.sin(s.angle), ShapeKind.box, s.radius, 0, width);
      return s.hw > 0 && s.hh > 0;
    case "hexagon":
      set(s.x, s.y, s.r, s.r, 1, 0, ShapeKind.hexagon, 0, 0, width);
      return s.r > 0;
    case "triangle":
    case "chevron": {
      // The frame's centre is halfway from the base to the tip; its x axis points to the tip.
      const half = s.length / 2;
      const chevron = s.type === "chevron";
      set(s.x - s.ux * half, s.y - s.uy * half, half, s.spread, s.ux, s.uy, chevron ? ShapeKind.chevron : ShapeKind.triangle, chevron ? s.paint.width : 0, 0, chevron ? 0 : width);
      return half > 0;
    }
    case "segment": {
      const len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1);
      if (len === 0 && !s.round) return false;
      const ax = len ? (s.x2 - s.x1) / len : 1;
      const ay = len ? (s.y2 - s.y1) / len : 0;
      set((s.x1 + s.x2) / 2, (s.y1 + s.y2) / 2, len / 2, s.paint.width / 2, ax, ay, s.round ? ShapeKind.roundSegment : ShapeKind.segment, on, off, 0);
      return true;
    }
  }
}

/**
 * Writes the colours (numbers 10 to 17). Lines and chevrons are drawn as filled shapes in the
 * outline's colour: their "inside" is the line itself.
 */
function colours(d: Float32Array, at: number, s: FlatShape, paint: Paint, color: ColorReader): void {
  const line = s.type === "segment" || s.type === "chevron";
  const fill = line ? paint.stroke : paint.fill;
  const stroke = line ? null : paint.stroke;
  d.set(fill ? premultiplied(color(fill), paint.alpha) : TRANSPARENT, at + 10);
  d.set(stroke && paint.width > 0 ? premultiplied(color(stroke), paint.alpha) : TRANSPARENT, at + 14);
}
